import retire from "./retire.ts";
import { idb } from "../../db/index.ts";
import { defaultTragicDeaths, g, helpers, logEvent, random } from "../../util/index.ts";
import type { Conditions, Player } from "../../../../common/types.ts";
import { GAME_NAME } from "../../../../common/constants.ts";
import { getDefaultTragicDeathTextKey } from "../../../../common/tragicDeaths.ts";

const getReason = () => {
	const tragicDeaths = g.get("tragicDeaths") ?? defaultTragicDeaths;

	let reason = random.choice(tragicDeaths, (row) => row.frequency).reason;
	let textKey = getDefaultTragicDeathTextKey(reason);
	let textParams: Record<string, number | string> | undefined;
	if (reason === "SPECIAL_CLUE") {
		const suspect = random.choice([
			"Miss Scarlet",
			"Professor Plum",
			"Mrs. Peacock",
			"Reverend Green",
			"Colonel Mustard",
			"Mrs. White",
		]);
		const room = random.choice([
			"kitchen",
			"ballroom",
			"conservatory",
			"dining room",
			"cellar",
			"billiard room",
			"library",
			"lounge",
			"hall",
			"study",
		]);
		const weapon = random.choice([
			"candlestick",
			"dagger",
			"lead pipe",
			"revolver",
			"rope",
			"spanner",
		]);

		reason = `PLAYER_NAME was killed by ${suspect}, in the ${room}, with the ${weapon}.`;
		textKey = "tragicDeath.clue";
		textParams = { suspect, room, weapon };
	} else if (reason === "SPECIAL_GIFTS") {
		const gifts = [
			"basketball shorts",
			"jerseys",
			"athletic socks",
			"knee sleeves",
			"elbow sleeves",
			"compression pants",
			"ankle braces",
			"knee braces",
			"sneakers",
			"headbands",
		];
		const gift1 = random.choice(gifts);
		const gift2 = random.choice(gifts.filter((gift) => gift !== gift1));

		// Draco, an Athenian lawmaker, was reportedly smothered to death by gifts of cloaks and hats showered upon him by appreciative citizens
		reason = `PLAYER_NAME was smothered to death by gifts of ${gift1} and ${gift2} showered upon ${helpers.pronoun(
			g.get("gender"),
			"him",
		)} by appreciative fans.`;
		textKey = "tragicDeath.gifts";
		textParams = { gift1, gift2 };
	}

	return {
		reason,
		textKey,
		textParams,
	};
};

const killOne = async (conditions: Conditions, player?: Player) => {
	let p: Player;
	let tid;
	if (!player) {
		// Pick random team
		const teams = (await idb.cache.teams.getAll()).filter((t) => !t.disabled);
		tid = random.choice(teams).tid;
		const players = await idb.cache.players.indexGetAll("playersByTid", tid);

		// Pick a random player on that team
		p = random.choice(players.filter((p) => !p.real));
		if (!p) {
			// Could happen, with real rosters
			return;
		}
	} else {
		p = player;
		tid = player.tid;
	}

	await retire(p, conditions, {
		logRetiredEvent: false,
	});

	const { reason, textKey, textParams } = getReason();

	const playerHtml = `<a href="${helpers.leagueUrl(["player", p.pid])}">${p.firstName} ${p.lastName}</a>`;
	const text = reason
		.replace(
			"PLAYER_NAME",
			playerHtml,
		)
		.replace(/PRONOUN_(\w*)/g, (match, pronoun) => {
			return helpers.pronoun(g.get("gender"), pronoun);
		});

	const pronounParams = {
		He: helpers.pronoun(g.get("gender"), "He"),
		he: helpers.pronoun(g.get("gender"), "he"),
		him: helpers.pronoun(g.get("gender"), "him"),
		his: helpers.pronoun(g.get("gender"), "his"),
		himself: helpers.pronoun(g.get("gender"), "himself"),
	};

	p.diedYear = g.get("season");
	await idb.cache.players.put(p);
	logEvent(
		{
			type: "tragedy",
			text,
			textKey,
			textParams: textKey
				? {
						gender: g.get("gender"),
						gameName: GAME_NAME,
						playerHtml,
						...pronounParams,
						...textParams,
					}
				: undefined,
			showNotification: tid === g.get("userTid"),
			pids: [p.pid],
			tids: [tid],
			persistent: true,
			score: 20,
		},
		conditions,
	);
};

export default killOne;
